import ModalResponsive from '@/components/commons/modal-responsive';
import { ExternalLinkIcon, CheckCircleIcon, PlusIcon, SearchIcon } from 'lucide-react';
import clsx from 'clsx';
import Link from 'next/link';
import { IProduct } from '@/types/product';
import { FallbackImage } from '@/components/commons/fallback-image';
import { useParams } from 'next/navigation';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { useDebounceValue } from '@/hooks/use-debounce';
import { useProducts } from '@/hooks/api/dashboard/products';

export interface ModalSelectProductProps {
  openModal?: boolean;
  setOpenModal?: (open: boolean) => void;
  onSelect?: (collection: Partial<IProduct>) => void;
  initialValues?: Partial<IProduct>;
}

export default function ModalSelectProduct({
  openModal,
  setOpenModal,
  onSelect,
  initialValues,
}: ModalSelectProductProps) {
  const [keyword, setKeyword] = useState('');
  const { storeId } = useParams<{ storeId: string }>();

  const q = useDebounceValue(keyword, 500);

  const { data: products } = useProducts({
    page: 1,
    pageSize: 20,
    q,
  });

  return (
    <ModalResponsive
      openModal={openModal}
      title='Select Product'
      setOpenModal={setOpenModal}
      className='max-w-md'
    >
      <div className='px-6'>
        <Input
          name='keyword'
          type='text'
          size={100}
          placeholder='Search'
          leftContent={<SearchIcon className='mx-auto w-4' />}
          onChange={(e) => setKeyword(e.target.value)}
        />
      </div>
      <div className='h-[600px] overflow-auto pb-4'>
        <Link
          href={`/store/${storeId}/product/unit/add`}
          target='_blank'
          className={clsx(
            'flex w-full items-center justify-between px-6 py-4 text-left hover:bg-gray-100'
          )}
        >
          <div className='flex items-center gap-x-4 text-primary'>
            <PlusIcon className='h-4 w-4' />
            Create Product
          </div>
          <ExternalLinkIcon className='h-4 w-4 text-primary' />
        </Link>
        {products?.map((product) => {
          const selected = initialValues?.id === product.id;
          return (
            <button
              key={product.id}
              onClick={() => {
                onSelect?.(product);
                setOpenModal?.(false);
              }}
              className={clsx('group flex w-full items-center justify-between px-6 py-4 text-left')}
            >
              <div className='flex items-center'>
                <FallbackImage
                  src={
                    product.product_assets && product.product_assets.length > 0
                      ? product.product_assets?.[0]?.assets_url
                      : 'https://placehold.co/400'
                  }
                  alt={product.product_name}
                  width={41}
                  height={41}
                  className='mr-2 h-[41px] w-[41px] rounded border object-contain'
                />

                {product.product_name}
              </div>
              {selected ? (
                <CheckCircleIcon className='h-5 w-5 text-primary' />
              ) : (
                <CheckCircleIcon className='h-5 w-5 opacity-0 duration-100 group-hover:opacity-100' />
              )}
            </button>
          );
        })}
      </div>
    </ModalResponsive>
  );
}
